import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import TMISLogo from '../img/tmis_logo_orig.png';
import TMISLogoW from '../img/tmis_logo_w.png';
import axios from 'axios';
import moment from 'moment-timezone'

const API = process.env.REACT_APP_API_URL || '';
const COOKIE_EMAIL = 'tmis_student_email';
const COOKIE_PHONE = 'tmis_student_phone';
const COOKIE_NAME = 'tmis_student_name';
// Cookie expiry is loaded from /api/settings at runtime

function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/;SameSite=Lax`;
}

function getCookie(name) {
  const val = document.cookie.split('; ').find(r => r.startsWith(name + '='));
  return val ? decodeURIComponent(val.split('=')[1]) : null;
}

function clearCookies() {
  setCookie(COOKIE_EMAIL, '', -1);
  setCookie(COOKIE_PHONE, '', -1);
}

function hashParts(parts) {
  const str = parts.join('|');
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (Math.imul(31, hash) + str.charCodeAt(i)) | 0;
  return hash.toString(16);
}

// Facts read straight from the OS/hardware rather than the browser's rendering
// engine — so, unlike getDeviceFingerprint(), this stays the SAME across different
// browsers or apps on the same physical device. Two genuinely identical devices
// (same model, same region settings) can still collide on this, so it's a
// correlation signal for review, not proof of identity on its own.
function getHardwareSignature() {
  const parts = [
    window.screen.width + 'x' + window.screen.height,
    window.screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 'na',
    navigator.deviceMemory || 'na',
    navigator.maxTouchPoints || 0,
    ('ontouchstart' in window) ? 'touch' : 'notouch',
    navigator.language,
    (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || 'na',
  ];
  return hashParts(parts);
}

// Builds a fingerprint from signals read live off the hardware/GPU/OS each call,
// so it survives cache/cookie clears and incognito/private mode (nothing here is
// stored client-side). It can NOT match across different browsers or apps on the
// same device — that sandboxing is enforced by the OS/browser vendors and no
// client-side script can see across it.
function getDeviceFingerprint() {
  const parts = [];

  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(0, 0, 100, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('tmis-fp-🔒', 2, 2);
    parts.push(canvas.toDataURL());
  } catch (_) { parts.push('no-canvas'); }

  try {
    const gl = document.createElement('canvas').getContext('webgl')
      || document.createElement('canvas').getContext('experimental-webgl');
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    parts.push(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL));
    parts.push(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL));
  } catch (_) { parts.push('no-webgl'); }

  try {
    const AudioCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    const actx = new AudioCtx(1, 1, 44100);
    parts.push(actx.sampleRate, actx.destination.channelCount, actx.destination.maxChannelCount);
  } catch (_) { parts.push('no-audio'); }

  try {
    const testFonts = ['Arial', 'Verdana', 'Times New Roman', 'Courier New', 'Georgia', 'Comic Sans MS', 'Impact', 'Trebuchet MS'];
    const span = document.createElement('span');
    span.style.cssText = 'position:absolute;left:-9999px;font-size:72px;';
    span.innerHTML = 'mmmmmmmmmmlli';
    document.body.appendChild(span);
    span.style.fontFamily = 'monospace';
    const baseWidth = span.offsetWidth;
    const detected = testFonts.filter(f => {
      span.style.fontFamily = `${f}, monospace`;
      return span.offsetWidth !== baseWidth;
    });
    document.body.removeChild(span);
    parts.push(detected.join(','));
  } catch (_) { parts.push('no-fonts'); }

  parts.push(
    navigator.userAgent, navigator.language,
    window.screen.width + 'x' + window.screen.height, window.screen.colorDepth,
    new Date().getTimezoneOffset(), navigator.hardwareConcurrency,
    navigator.deviceMemory || 'na', navigator.maxTouchPoints,
    ('ontouchstart' in window) ? 'touch' : 'notouch'
  );

  return hashParts(parts);
}

export default function ScanPage() {
  const { qrId } = useParams();
  const { theme } = useTheme();

  // steps: loading | confirm | email | phone | success | error | wifi-error
  const [step, setStep] = useState('loading');
  const [session, setSession] = useState(null);
  const [cookieExpiryDays, setCookieExpiryDays] = useState(365);
  const [allowPhoneChange, setAllowPhoneChange] = useState(false);
  const [type, setType] = useState('checkin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const emailRef = useRef();

  useEffect(() => {
    if (!qrId) { setStep('error'); setError('Invalid QR code.'); return; }

    // Fetch system settings (cookie expiry, allow phone change)
    axios.get(`${API}/api/settings`)
      .then(res => {
        const s = res.data.data || {};
        setCookieExpiryDays(parseInt(s.cookie_expiry_days) || 365);
        setAllowPhoneChange(s.allow_phone_change === 'true');
      })
      .catch(() => {}); // use defaults on failure

    axios.get(`${API}/api/attendance/session/${qrId}`)
      .then(res => {
        let sDate = moment(res.data.session.session_date).format('DD/MM/YYYY')
        res.data.session.session_date = sDate
        setSession(res.data.session);
        setType(res.data.type);

        // const storedEmail = getCookie(COOKIE_EMAIL);
        const storedPhone = getCookie(COOKIE_PHONE);

        if (storedPhone) {
          setName(getCookie(COOKIE_NAME))
          setPhone(storedPhone);
          setStep('confirm');
        } else {
          // Nothing saved — full flow
          setStep('phone');
        }

        // if (storedEmail && storedPhone) {
        //   // Both saved — go straight to confirm screen
        //   setEmail(storedEmail);
        //   setPhone(storedPhone);
        //   setStep('confirm');
        // } else if (storedEmail) {
        //   // Email saved but no phone — ask for phone only
        //   setEmail(storedEmail);
        //   setStep('phone');
        // } else {
        //   // Nothing saved — full flow
        //   setStep('email');
        // }
      })
      .catch(err => {
        setStep('error');
        setError(err.response?.data?.message || 'This QR code is not valid or the session has ended.');
      });
  }, [qrId]);

  useEffect(() => {
    if (step === 'email' && emailRef.current) emailRef.current.focus();
  }, [step]);

  function handleEmailSubmit(e) {
    e.preventDefault();
    if (!email) { setMessage('Please enter your email.'); return; }
    setCookie(COOKIE_EMAIL, email.toLowerCase().trim(), cookieExpiryDays);
    setEmail(email.toLowerCase().trim());
    setMessage('');
    setStep('phone');
  }

  async function submitClock(emailVal, phoneVal) {
    setSubmitting(true);
    setMessage('');
    try {
      const res = await axios.post(`${API}/api/attendance/clock`, {
        qr_id: qrId,
        email: emailVal.toLowerCase().trim(),
        phone: phoneVal.trim(),
        device_fingerprint: getDeviceFingerprint(),
        hardware_signature: getHardwareSignature(),
        device_id: navigator.userAgent.replace(/[^a-z0-9]/gi, '').substring(0, 64),
      });
      // Save both cookies on success
      setCookie(COOKIE_EMAIL, emailVal.toLowerCase().trim(), cookieExpiryDays);
      setCookie(COOKIE_PHONE, phoneVal.trim(), cookieExpiryDays);
      setCookie(COOKIE_NAME, res.data.student.name, cookieExpiryDays);
      setResult(res.data);
      setStep('success');
    } catch (err) {
      const msg = err.response?.data?.message || 'An error occurred. Please try again.';
      if (msg.includes('network') || msg.includes('WiFi')) {
        setError(msg);
        setStep('wifi-error');
      } else {
        setMessage(msg);
        // If on confirm step, drop back to confirm so they can see the error
        if (step === 'confirm') setStep('confirm');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePhoneSubmit(e) {
    e.preventDefault();
    if (!phone) { setMessage('Please enter your phone number.'); return; }
    await submitClock(email, phone);
  }

  // Called from the confirm screen — one tap to clock in/out
  async function handleConfirmClock() {
    await submitClock(email, phone);
  }

  function handleChangeIdentity() {
    clearCookies();
    setEmail('');
    setPhone('');
    setMessage('');
    setStep('phone');
  }

  // ── Session info banner ────────────────────────────────────────
  const sessionInfo = session && (
    <div className="mb-4 p-3 rounded-3" style={{ background: '#f0f7ff', border: '1px solid #bee3f8' }}>
      <div className="fw-bold text-truncate" style={{ color: '#1a202c' }}>{session.schedule_name}</div>
      <div style={{ fontSize: '0.9rem', color: '#4a5568' }}>
        <i className="bi bi-calendar3 me-1" />{session.session_date}
        <span className="ms-3"><i className="bi bi-clock me-1" />{session.start_time}{session.end_time ? ` – ${session.end_time}` : ''}</span>
      </div>
      <div style={{ fontSize: '0.9rem', color: '#4a5568' }}>
        <i className="bi bi-geo-alt me-1" />{session.venue_name}
        {!session.is_outdoor && (
          <span className="ms-2 badge text-dark" style={{ fontSize: '0.9rem', backgroundColor:'#ffd24b' }}>WiFi Required</span>
        )}
      </div>
    </div>
  );

  const typeLabel = type === 'checkout' ? 'Clock Out' : 'Clock In';
  const typeIcon  = type === 'checkout' ? 'bi-box-arrow-right' : 'bi-box-arrow-in-right';

  return (
    <div className="scan-wrapper">
      <div className="scan-card">

        {/* Header */}
        <div className="text-center mb-4">
          <img src={theme === 'dark' ? TMISLogoW : TMISLogo} alt="TMIS Logo" className="w-50" />
        </div>

        {/* ── LOADING ── */}
        {step === 'loading' && (
          <div className="text-center py-4">
            <div className="spinner-border" style={{ color: '#17a2b8' }} />
            <p className="mt-2 text-muted">Verifying QR code…</p>
          </div>
        )}

        {/* ── ERROR ── */}
        {step === 'error' && (
          <div className="text-center py-4">
            <i className="bi bi-qr-code d-block fs-1 text-danger mb-3" />
            <h5 className="text-danger">Invalid QR Code</h5>
            <p className="text-muted" style={{ fontSize: '0.95rem' }}>{error}</p>
          </div>
        )}

        {/* ── WIFI ERROR ── */}
        {step === 'wifi-error' && (
          <div className="text-center py-4">
            <i className="bi bi-wifi-off d-block fs-1 text-warning mb-3" />
            <h5>Not on Company Network</h5>
            <p className="text-muted" style={{ fontSize: '0.95rem' }}>{error}</p>
            <button className="btn btn-tmis-primary mt-2" onClick={() => { setError(''); setStep('confirm'); }}>
              Try Again
            </button>
          </div>
        )}

        {/* ── CONFIRM (cookie found — one-tap screen) ── */}
        {step === 'confirm' && (
          <>
            {sessionInfo}

            {/* Identity chip */}
            <div className="p-3 rounded-3 mb-4" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <i className="bi bi-person-circle" style={{ color: '#5a6a7e' }} />
                <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1a202c' }}>{name}</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-phone" style={{ color: '#5a6a7e' }} />
                <span style={{ fontSize: '0.95rem', color: '#1a202c' }}>{'•'.repeat(Math.max(0, phone.length - 4)) + phone.slice(-4)}</span>
              </div>
              {allowPhoneChange && (
                <button
                  className="btn btn-sm btn-link p-0 mt-2"
                  style={{ fontSize: '0.95rem', color: '#5a6a7e' }}
                  onClick={handleChangeIdentity}
                >
                  Not you? Change phone number
                </button>
              )}
            </div>

            {message && (
              <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: '0.95rem' }}>
                <i className="bi bi-exclamation-circle me-1" />{message}
              </div>
            )}

            <button
              className="btn btn-tmis-primary w-100 d-flex align-items-center justify-content-center gap-2"
              style={{ padding: '12px', fontSize: '1rem' }}
              onClick={handleConfirmClock}
              disabled={submitting}
            >
              {submitting
                ? <span className="spinner-border spinner-border-sm" />
                : <i className={`bi ${typeIcon}`} />
              }
              {typeLabel}
            </button>
          </>
        )}

        {/* ── EMAIL STEP ── */}
        {step === 'email' && (
          <>
            {sessionInfo}
            <h6 className="fw-bold mb-1">Enter your email</h6>
            <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
              Your details will be saved so you won't need to re-enter them next time.
            </p>
            {message && (
              <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: '0.95rem' }}>
                <i className="bi bi-exclamation-circle me-1" />{message}
              </div>
            )}
            <form onSubmit={handleEmailSubmit}>
              <div className="mb-3">
                <label className="form-label">Email address</label>
                <input
                  ref={emailRef}
                  type="email"
                  className="form-control"
                  placeholder="you@tmis.edu.sg"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setMessage(''); }}
                  required
                  autoComplete="email"
                />
              </div>
              <button type="submit" className="btn btn-tmis-primary w-100">
                Continue <i className="bi bi-arrow-right ms-1" />
              </button>
            </form>
          </>
        )}

        {/* ── PHONE STEP ── */}
        {step === 'phone' && (
          <>
            {sessionInfo}
            {/* Show saved email */}
            {/* <div className="mb-3 p-2 rounded-3 d-flex justify-content-between align-items-center"
              style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '0.95rem' }}>
                <i className="bi bi-person-circle me-1 text-muted" />
                <strong>{email}</strong>
              </div>
              <button className="btn btn-sm btn-link p-0" style={{ fontSize: '0.95rem' }} onClick={handleChangeIdentity}>
                Change
              </button>
            </div> */}
            <h6 className="fw-bold mb-1">Verify your phone number</h6>
            <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
              Enter your 8-digit registered phone number to verify your identity.
            </p>
            {message && (
              <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: '0.95rem' }}>
                <i className="bi bi-exclamation-circle me-1" />{message}
              </div>
            )}
            <form onSubmit={handlePhoneSubmit}>
              <div className="mb-3">
                <label className="form-label">Phone number</label>
                <input
                  type="tel"
                  className="form-control"
                  placeholder="e.g. 91234567"
                  maxLength="8"
                  value={phone}
                  onChange={e => { setPhone(e.target.value); setMessage(''); }}
                  autoComplete="tel"
                />
                <div className="form-text">Enter your 8-digit Singapore phone number.</div>
              </div>
              <button
                type="submit"
                className="btn btn-tmis-primary w-100 d-flex align-items-center justify-content-center gap-2"
                disabled={submitting}
              >
                {submitting && <span className="spinner-border spinner-border-sm" />}
                <i className={`bi ${typeIcon}`} />
                {typeLabel}
              </button>
            </form>
          </>
        )}

        {/* ── SUCCESS ── */}
        {step === 'success' && (
          <div className="text-center py-3">
            <div className="mb-3" style={{
              width: 72, height: 72, borderRadius: '50%',
              background: type === 'checkout' ? '#d4edda' : '#d1ecf1',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto',
            }}>
              <i className="bi bi-check-lg fs-2" style={{ color: type === 'checkout' ? '#198754' : '#0d6efd' }} />
            </div>
            <h5 className="fw-bold">{type === 'checkout' ? 'Clocked Out!' : 'Clocked In!'}</h5>
            <p className="text-muted">{result?.message}</p>
            {result?.student && (
              <div className="p-3 rounded-3" style={{ background: '#f8fafc', fontSize: '0.95rem' }}>
                <div className="fw-semibold" style={{ color: '#1a202c' }}>{result.student.name}</div>
                <div style={{ color: '#5a6a7e' }}>{result.student.intake}</div>
              </div>
            )}
            <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.95rem' }}>
              <i className="bi bi-clock me-1" />
              {new Date().toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
          </div>
        )}

        <div className="text-center mt-4 text-muted" style={{ fontSize: '0.9rem' }}>
          TMIS QR Attendance System · Authorised personnel only
        </div>
      </div>
    </div>
  );
}
